// Lesende Hilfsfunktionen rund um das Datenmodell (keine Seiteneffekte).
import { layoutProcess } from './layout/index.js';

export const NODE_TYPES = [
  { id: 'start', name: 'Start' },
  { id: 'ende', name: 'Ende' },
  { id: 'aktivitaet', name: 'Aktivität' },
  { id: 'automatik', name: 'Automatismus / Schnittstelle' },
  { id: 'beleg', name: 'Beleg' },
  { id: 'gateway', name: 'Verzweigung' },
  { id: 'entscheidung', name: 'Offene Entscheidung' },
  { id: 'subprozess', name: 'Unterprozess' },
];

export const STATUS = [
  { id: 'umgesetzt', name: 'Eingerichtet', farbe: '#2E8B57' },
  { id: 'in_arbeit', name: 'In Arbeit', farbe: '#D49A00' },
  { id: 'offen', name: 'Offen', farbe: '#C0392B' },
  { id: 'freigabe_offen', name: 'Freigabe offen', farbe: '#8E44AD' },
  { id: 'nicht_angeboten', name: 'Nicht angeboten', farbe: '#9AA7B0' },
];
export const statusOf = (id) => STATUS.find((s) => s.id === id);
export const DECISION_COLOR = '#E67E22';

export const byId = (arr, id) => arr.find((x) => x.id === id);

export function processPath(data, pid) {
  const path = [];
  const seen = new Set();
  let p = byId(data.processes, pid);
  while (p && !seen.has(p.id)) {
    seen.add(p.id);
    path.unshift(p);
    p = p.parentId ? byId(data.processes, p.parentId) : null;
  }
  return path;
}

export function children(data, pid) {
  return data.processes
    .filter((p) => (p.parentId || null) === (pid || null))
    .sort((a, b) => (a.order ?? 1e9) - (b.order ?? 1e9) || (a.titel < b.titel ? -1 : 1));
}

export function descendants(data, pid) {
  const res = [];
  const walk = (id) => {
    for (const c of children(data, id)) {
      res.push(c);
      walk(c.id);
    }
  };
  walk(pid);
  return res;
}

/** Prozess plus alle Nachfahren in Lesereihenfolge (Tiefensuche). */
export function processTree(data, pid) {
  const p = byId(data.processes, pid);
  return p ? [p, ...descendants(data, pid)] : [];
}

export function nodesOf(data, pid, { zielbild = false } = {}) {
  return data.nodes.filter((n) => n.processId === pid && (zielbild || !n.nurZielbild));
}

export function edgesOf(data, pid, nodeIds) {
  return data.edges.filter((e) => e.processId === pid && (!nodeIds || (nodeIds.has(e.from) && nodeIds.has(e.to))));
}

export function laneOrderOf(data, process, nodes) {
  const order = [...(process.laneIds || [])];
  for (const n of nodes) if (!order.includes(n.lane)) order.push(n.lane);
  return order.filter((l) => byId(data.lanes, l));
}

/** Knoten eines Prozesses in Lesereihenfolge (nach Spalte, dann Bahn). */
export function readingOrder(layout, nodes, laneOrder) {
  return [...nodes].sort((a, b) => {
    const pa = layout.nodes[a.id], pb = layout.nodes[b.id];
    return pa.rank - pb.rank || laneOrder.indexOf(a.lane) - laneOrder.indexOf(b.lane) || pa.row - pb.row;
  });
}

export function openDecisions(data) {
  return data.decisions.filter((d) => d.status !== 'entschieden');
}

export function decisionsInTree(data, pid) {
  const ids = new Set(processTree(data, pid).map((p) => p.id));
  return data.decisions.filter((d) => ids.has(d.processId));
}

/** Alle eingetragenen Verantwortlichen (Schritte und Entscheidungen) mit Anzahl der Verwendungen. */
export function verantwortliche(data) {
  const m = new Map();
  for (const x of [...data.nodes, ...data.decisions]) {
    const v = (x.verantwortlich || '').trim();
    if (v) m.set(v, (m.get(v) || 0) + 1);
  }
  return [...m].map(([name, anzahl]) => ({ name, anzahl })).sort((a, b) => a.name.localeCompare(b.name, 'de'));
}

export function newId(prefix, data) {
  const taken = new Set([
    ...data.processes.map((x) => x.id),
    ...data.nodes.map((x) => x.id),
    ...data.edges.map((x) => x.id),
    ...data.decisions.map((x) => x.id),
    ...data.lanes.map((x) => x.id),
    ...data.systems.map((x) => x.id),
  ]);
  let id;
  do id = prefix + '_' + Math.random().toString(36).slice(2, 8);
  while (taken.has(id));
  return id;
}

export function search(data, q) {
  const s = q.trim().toLowerCase();
  if (s.length < 2) return [];
  const hit = (...fields) => fields.some((f) => f && String(f).toLowerCase().includes(s));
  const res = [];
  for (const p of data.processes) if (hit(p.titel, p.kurz, p.beschreibung)) res.push({ kind: 'prozess', p, titel: p.titel });
  for (const n of data.nodes) {
    if (hit(n.titel, n.beschreibung, n.notizen, n.zielbild, n.verantwortlich, (n.belege || []).join(' '))) {
      const p = byId(data.processes, n.processId);
      if (p) res.push({ kind: 'schritt', p, n, titel: n.titel });
    }
  }
  for (const d of data.decisions) {
    if (hit(d.frage, d.hintergrund, d.empfehlung)) {
      const p = byId(data.processes, d.processId);
      res.push({ kind: 'entscheidung', p, d, titel: d.frage });
    }
  }
  return res.slice(0, 40);
}

/** Layout eines Prozesses inkl. der dafür gefilterten Knoten und Kanten. */
export function layoutFor(data, pid, { zielbild = false } = {}) {
  const process = byId(data.processes, pid);
  const nodes = nodesOf(data, pid, { zielbild });
  const ids = new Set(nodes.map((n) => n.id));
  const edges = edgesOf(data, pid, ids);
  const laneOrder = laneOrderOf(data, process, nodes);
  const labelText = (e) => {
    const t = byId(data.edgeTypes, e.typ);
    return [e.label, t && t.kennung].filter(Boolean).join(' · ');
  };
  const layout = layoutProcess({ nodes, edges, laneOrder, labelText });
  return { process, nodes, edges, laneOrder, layout };
}
